import { Injectable, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Subject, Subscription, interval, of } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { EventosSocketService } from 'src/app/modules/citas/services/eventos-socket.service';
import { SocioOperacionEstatus } from '../models/administracion-socios.models';
import { AdministracionSociosService } from './administracion-socios.service';

const VISTOS_KEY = 'sdm.socioOp.vistos';

@Injectable({ providedIn: 'root' })
export class SocioOperacionEstadoService implements OnDestroy {
  private readonly jobs = new BehaviorSubject<SocioOperacionEstatus[]>([]);
  readonly jobs$ = this.jobs.asObservable();
  readonly chip$ = new BehaviorSubject<SocioOperacionEstatus | null>(null);
  readonly operacionTerminada$ = new Subject<SocioOperacionEstatus>();

  private pollSub?: Subscription;
  private socketSub?: Subscription;
  private iniciado = false;
  private vistos = new Set<string>();
  private pendientes = new Set<number>();

  constructor(
    private api: AdministracionSociosService,
    private snackBar: MatSnackBar,
    private eventos: EventosSocketService,
  ) {
    this.cargarVistos();
  }

  /** Un GET al entrar y socket de complemento. El intervalo solo corre con job abierto. */
  iniciar(): void {
    if (this.iniciado) {
      return;
    }
    this.iniciado = true;
    this.socketSub = this.eventos.socioOperacion$
      .pipe(filter((p) => !!p))
      .subscribe(() => this.refreshAhora());
    this.refreshAhora();
  }

  /** Marca un job enviado (POST 202) para avisar al terminar aunque el GET ya lo vea cerrado. */
  registrarPendiente(operacionId: number): void {
    if (operacionId) {
      this.pendientes.add(operacionId);
    }
    this.refreshAhora();
  }

  /** Fuerza un GET inmediato (p.ej. tras POST 202 o evento socket). */
  refreshAhora(): void {
    this.api.estatusOperacion().pipe(catchError(() => of([] as SocioOperacionEstatus[]))).subscribe((lista) => {
      this.aplicarLista(lista);
    });
  }

  /** Texto del chip según acción. */
  mensajeChip(job: SocioOperacionEstatus | null): string {
    if (!job) {
      return '';
    }
    const rfc = (job.rfc || '').trim() || 'socio';
    if (job.accion === 'eliminar-migrar') {
      return `Eliminando y migrando ${rfc}…`;
    }
    if (job.accion === 'restaurar') {
      return `Restaurando socio ${rfc}…`;
    }
    return `Eliminando socio ${rfc}…`;
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
    this.socketSub?.unsubscribe();
    this.jobs.complete();
    this.chip$.complete();
    this.operacionTerminada$.complete();
  }

  private aplicarLista(lista: SocioOperacionEstatus[]): void {
    this.jobs.next(lista);
    const enProceso = lista.find((j) => j.estatus === 'EN_PROCESO') ?? null;
    this.chip$.next(enProceso);
    for (const job of lista) {
      if (job.estatus === 'EN_PROCESO') {
        this.pendientes.add(job.operacion_id);
        continue;
      }
      if (!this.pendientes.has(job.operacion_id)) {
        continue;
      }
      const clave = `${job.operacion_id}:${job.estatus}`;
      if (this.vistos.has(clave)) {
        continue;
      }
      this.vistos.add(clave);
      this.pendientes.delete(job.operacion_id);
      this.guardarVistos();
      this.avisarTermino(job);
      this.operacionTerminada$.next(job);
    }
    this.ajustarSondeo(lista);
  }

  /** Enciende el intervalo de 5 s solo mientras haya un job abierto o pendiente de aviso. */
  private ajustarSondeo(lista: SocioOperacionEstatus[]): void {
    const hayAbiertos =
      lista.some((j) => j.estatus === 'EN_PROCESO') || this.pendientes.size > 0;
    if (hayAbiertos) {
      this.encenderSondeo();
      return;
    }
    this.apagarSondeo();
  }

  private encenderSondeo(): void {
    if (this.pollSub) {
      return;
    }
    this.pollSub = interval(5000)
      .pipe(
        switchMap(() =>
          this.api.estatusOperacion().pipe(catchError(() => of([] as SocioOperacionEstatus[]))),
        ),
      )
      .subscribe((lista) => this.aplicarLista(lista));
  }

  private apagarSondeo(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  private avisarTermino(job: SocioOperacionEstatus): void {
    if (job.estatus === 'ERROR') {
      this.snackBar.open(
        job.error_detalle || 'La operación de socio falló.',
        'Cerrar',
        { duration: 8000, panelClass: ['error-alert'] },
      );
      return;
    }
    const okMsg =
      job.accion === 'restaurar' || job.estatus === 'RESTAURADO'
        ? 'Restauración aplicada correctamente.'
        : 'Operación aplicada correctamente.';
    this.snackBar.open(okMsg, 'Cerrar', { duration: 4000 });
    const idPdf =
      job.accion === 'restaurar' || job.estatus === 'RESTAURADO'
        ? job.restauracion_id || job.operacion_id
        : job.operacion_id;
    const variante: 'eliminacion' | 'restauracion' =
      job.accion === 'restaurar' || job.estatus === 'RESTAURADO' ? 'restauracion' : 'eliminacion';
    this.descargarPdf(idPdf, job.rfc || '', variante);
  }

  private descargarPdf(
    operacionId: number,
    rfc: string,
    variante: 'eliminacion' | 'restauracion',
  ): void {
    this.api.descargarReporteAuditoria(operacionId).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.nombreArchivoAcuse(rfc, variante);
        a.click();
        URL.revokeObjectURL(url);
      },
      error: (err: Error) => {
        this.snackBar.open(err.message || 'No se pudo obtener el PDF', 'Cerrar', {
          duration: 5000,
          panelClass: ['error-alert'],
        });
      },
    });
  }

  private nombreArchivoAcuse(rfc: string, variante: 'eliminacion' | 'restauracion'): string {
    const prefijo = variante === 'restauracion' ? 'Acuse_Restauracion' : 'Acuse_Eliminacion';
    const rfcLimpio = (rfc || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase() || 'SINRFC';
    return `${prefijo}_${rfcLimpio}.pdf`;
  }

  private cargarVistos(): void {
    try {
      const raw = sessionStorage.getItem(VISTOS_KEY);
      const arr = raw ? (JSON.parse(raw) as string[]) : [];
      this.vistos = new Set(Array.isArray(arr) ? arr : []);
    } catch {
      this.vistos = new Set();
    }
  }

  private guardarVistos(): void {
    try {
      sessionStorage.setItem(VISTOS_KEY, JSON.stringify(Array.from(this.vistos)));
    } catch {
      /* ignore quota */
    }
  }
}
